// Centralised error-forwarding helpers. The Sentry loader script in main.tsx
// installs `window.Sentry` once the DSN env var is set; until then these are
// no-ops. We never throw from here — these are best-effort observability hooks
// that run alongside the normal error-handling path (banner/toast/throw).
//
// Use this so we have ONE place that knows the loader-script API, instead of
// the `(window as any).Sentry?.captureException?.(err)` pattern sprinkled
// across the codebase.

/* eslint-disable @typescript-eslint/no-explicit-any */

type SentryLevel = "fatal" | "error" | "warning" | "info" | "debug";

interface ReportOptions {
  /** Free-form tag describing where the error came from, e.g. "email.send". */
  scope?: string;
  /** Sentry severity. Defaults to "error". Use "warning" for best-effort paths. */
  level?: SentryLevel;
  /** Extra structured context attached to the Sentry event. */
  context?: Record<string, unknown>;
}

/**
 * Forward a thrown value to Sentry without re-throwing. Safe to call before
 * Sentry has finished loading — the loader queues calls until ready.
 */
export function reportError(err: unknown, opts: ReportOptions = {}): void {
  try {
    const Sentry = (window as any).Sentry;
    if (!Sentry?.captureException) return;
    const extras: Record<string, unknown> = {
      ...(opts.context ?? {}),
    };
    if (opts.scope) extras.scope = opts.scope;
    Sentry.captureException(err, {
      level: opts.level ?? "error",
      extra: extras,
      tags: opts.scope ? { scope: opts.scope } : undefined,
    });
  } catch {
    /* never throw from observability code */
  }
}

/**
 * Convenience for best-effort background work (email send, analytics ping).
 * Logs to console.warn AND forwards to Sentry at "warning" level. Does NOT
 * throw — callers are expected to swallow these intentionally because the
 * primary user action already succeeded.
 */
export function reportBackgroundFailure(
  scope: string,
  err: unknown,
  context?: Record<string, unknown>,
): void {
  // Console warn so devs see it locally; the prefix mirrors the existing
  // `[contracts] sign email failed:` style across the codebase.
  console.warn(`[${scope}] ${(err as Error)?.message ?? err}`, err);
  reportError(err, { scope, level: "warning", context });
}
